import { Flex, IconButton, Separator } from "@radix-ui/themes";
import {
  ExploreCustomField,
  ExploreInteractionType,
  exploreInteractionTypeToLabel,
} from "@/v2/entities/explore/Explore.model";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import DateRangePicker from "@/components/shared/date-range-picker-v2";
import { ExploreSearch } from "./ExploreSearch";
import { ExploreGroupByColumnType } from "@/v2/entities/explore/columns/ExploreColum.model";
import { ExploreFilterMenu } from "../explore-filter-menu/ExploreFilterMenu";
import { useExploreState } from "./useExploreState";
import { Tabs, TabsList, TabsTrigger } from "@/v2/components/dataTabs/tabs";
import { LayoutPanelTop, Table2 } from "lucide-react";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { ExploreGroupByPickerTabs } from "./ExploreGroupByPicker";
import { PropsWithChildren, useCallback } from "react";
import { ReloadIcon } from "@radix-ui/react-icons";
import { AskAnythingButton } from "./ask-anything/AskAnythingButton";

interface ExploreHeaderProps extends ReturnType<typeof useExploreState> {
  viewMode: "both" | "table" | "charts";
  source: ExploreInteractionType;
  filter: Filter;
  search: string;
  isEvaluated: boolean;
  allowedGroupByOptions: ExploreGroupByColumnType[];
  notAllowedGroupByOptions: ExploreGroupByColumnType[];
  conversationCustomFields?: ExploreCustomField[];
  ticketCustomFields?: ExploreCustomField[];
  setViewMode: (viewMode: "both" | "table" | "charts") => void;
  setSearch: (search: string) => void;
  isAskAnythingOpen: boolean;
  setIsAskAnythingOpen: (isOpen: boolean) => void;
}

export function ExploreHeader({
  state,
  viewMode,
  source,
  filter,
  search,
  isEvaluated,
  allowedGroupByOptions,
  notAllowedGroupByOptions,
  conversationCustomFields,
  ticketCustomFields,
  handleGroupByChange,
  handleFiltersChange,
  handleDateRangeChange,
  reparseDateRange,
  setViewMode,
  setSearch,
  children,
  isAskAnythingOpen,
  setIsAskAnythingOpen,
}: PropsWithChildren<ExploreHeaderProps>) {
  const hasWidgets = useFlag("explore-widget");
  const hasAskQuack = useFlag("exploreAsk");

  const { groupBy, dateRange, filters } = state;
  const handleAskAnythingClick = useCallback(() => {
    setIsAskAnythingOpen(!isAskAnythingOpen);
  }, [isAskAnythingOpen, setIsAskAnythingOpen]);

  return (
    <Flex
      width={"calc(100% - 250px)"}
      direction={"column"}
      position={"fixed"}
      top={"0"}
      className="bg-background z-40"
    >
      <Flex
        justify={"between"}
        align={"center"}
        px={"3"}
        py={"6px"}
        overflow={"hidden"}
        gap={"2"}
      >
        <Flex flexGrow={"1"} flexShrink={"1"} overflow={"hidden"}>
          <ExploreGroupByPickerTabs
            source={source}
            groupBy={groupBy}
            setGroupBy={handleGroupByChange}
            allowedGroupByOptions={allowedGroupByOptions}
            notAllowedGroupByOptions={notAllowedGroupByOptions}
            conversationCustomFields={conversationCustomFields}
            ticketCustomFields={ticketCustomFields}
          />
        </Flex>

        <Flex flexShrink={"0"} gap={"2"}>
          {children}
        </Flex>
      </Flex>

      <Separator
        orientation="horizontal"
        size="4"
        className="bg-[--q-border]"
      />

      <Flex px={"3"} py={"2"} justify={"between"} gap={"2"}>
        <ExploreFilterMenu
          size="2"
          source={source}
          value={filters}
          onChange={handleFiltersChange}
          label={
            isEvaluated
              ? `Evaluated ${exploreInteractionTypeToLabel[source].plural}`
              : `All ${exploreInteractionTypeToLabel[source].plural}`
          }
          filter={filter}
        />

        <Flex justify={"between"} gap={"2"} flexShrink={"0"} align={"center"}>
          {hasAskQuack && (
            <AskAnythingButton onClick={handleAskAnythingClick} />
          )}
          <ExploreSearch search={search} onSearchChanged={setSearch} />

          <IconButton
            variant="surface"
            color="gray"
            size="2"
            onClick={reparseDateRange}
          >
            <ReloadIcon />
          </IconButton>

          <DateRangePicker
            size="2"
            value={dateRange}
            onChange={handleDateRangeChange}
          />

          {hasWidgets && (
            <Tabs
              value={viewMode}
              onValueChange={(value) =>
                setViewMode(value as "both" | "table" | "charts")
              }
            >
              <TabsList size="md">
                <TabsTrigger value="table" size="md">
                  <Table2
                    size={20}
                    strokeWidth={viewMode === "table" ? 2.5 : 1.5}
                  />
                </TabsTrigger>
                <TabsTrigger value="both" size="md">
                  <LayoutPanelTop
                    size={20}
                    strokeWidth={viewMode === "both" ? 2.5 : 1.5}
                  />
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}
        </Flex>
      </Flex>

      <Separator
        orientation="horizontal"
        size="4"
        className="bg-[--q-border]"
      />
    </Flex>
  );
}
